import React, { Fragment, useEffect, useState } from "react";
import { Wizard } from "components/wizard";
import { GridLayout } from "components/grid/GridLayout";
import {
    FormCheckbox,
    FormDateInput,
    FormMultiSelect,
    FormFileInput,
    FormNumericInput,
} from "components/form-fields";
import { Button } from "components/buttons";
import { TypesOfCoverRecord } from "pages/licence-checking/grey-fleet/shared";
import { ShowHide } from "components/layout";
import {
    AlertConfirm,
    FailurePanel,
    NotificationToaster,
    SuccessPanel,
} from "components/notifications";
import { useQuery } from "hooks/useQuery";
import { useValidation } from "hooks/useValidation";
import { GreyFleetService, LicenceCheckingService } from "services";
import { Intent } from "@blueprintjs/core";
import { UnauthenticatedPage } from "pages/layouts";
import moment from "moment";
import classNames from "classnames";

export function GreyFleetInsuranceDetails() {

    const query = useQuery();
    const employee = query.get("employee");
    const code = query.get("code");

    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [wizardStep, setWizardStep] = useState(0);
    const [wizardValid, setWizardValid] = useState(true);
    const [wizardIsAllowedBack, setWizardIsAllowedBack] = useState(true);
    const [isValid, errors, validate] = useValidation();
    const [isConsentSuccess, setIsConsentSuccess] = useState(false);
    const [isConsentRejected, setIsConsentRejected] = useState(false);
    const [isConsentError, setIsConsentError] = useState(false);
    const [isRefuseConsentModalVisible, setIsRefuseConsentModalVisible] =
        useState(false);

    const [accountName, setAccountName] = useState("");
    const [registration, setRegistration] = useState("");
    const [declarationStepRequired, setDeclarationStepRequired] = useState(false);
    const [declarationText, setDeclarationText] = useState("");

    const [coverTypes, setCoverTypes] = useState([]);
    const [isLoadingCoverTypes, setIsLoadingCoverTypes] = useState(true);
    const [requiredCoverTypeIds, setRequiredCoverTypeIds] = useState([]);

    const [insuranceDetails, setInsuranceDetails] = useState({
        coverTypeIds: [],
        startDate: null,
        endDate: null,
        file: null,
        filename: null,
        odometerReading: null,
        declarationSigned: false,
    });

    const [isRegConfirmed, setIsRegConfirmed] = useState(false);

    // A required type missing from the loaded lookup can be neither shown nor selected, so it is
    // dropped here. Both the block the driver reads and the rule that holds them to it work from
    // this one list, so the two can never disagree about what is being asked for.
    const resolvedRequiredCoverTypes = requiredCoverTypeIds
        .map((coverTypeId) => coverTypes.find((coverType) => coverType.id === coverTypeId))
        .filter((coverType) => coverType != null);
    const resolvedRequiredCoverTypeIds = resolvedRequiredCoverTypes.map((coverType) => coverType.id);

    useEffect(() => {
        if (!employee || !code) {
            return;
        }

        getInsuranceDetails();
    }, [employee, code]);

    // The cover types are static reference data rather than anything tied to the consent link, so
    // they are fetched once regardless of the token in the query string.
    useEffect(() => {
        getInsuranceCoverTypes();
    }, []);

    useEffect(validateWizard, [
        insuranceDetails,
        isValid,
        wizardStep,
        isLoading,
        requiredCoverTypeIds,
        coverTypes,
    ]);

    useEffect(() => {
        setWizardValid((wizardStep === 0 || isValid) && isRegConfirmed);
    }, [wizardStep, isValid, isRegConfirmed]);

    function validateWizard() {

        let rules = [];
        let externalRules = [];

        if (wizardStep === 1 || wizardStep === 2) {
            rules.push({ fieldName: "startDate", required: true });
            rules.push({ fieldName: "endDate", required: true });
            rules.push({ fieldName: "filename", required: true });
            rules.push({ fieldName: "odometerReading", required: true });

            externalRules.push({ fieldName: "coverTypeIds", valid: insuranceDetails.coverTypeIds.length > 0, errorMessage: "At least one type of cover must be selected." });

            // Only the first failing external rule per field is kept, so this sits after the rule above
            // to leave the empty selection reporting that nothing is selected rather than what is missing.
            const hasEveryRequiredCoverType = resolvedRequiredCoverTypeIds.every((coverTypeId) =>
                insuranceDetails.coverTypeIds.includes(coverTypeId));
            externalRules.push({ fieldName: "coverTypeIds", valid: hasEveryRequiredCoverType, errorMessage: "Your insurance should cover all required types" });

            if (insuranceDetails.startDate && insuranceDetails.endDate) {
                rules.push({ fieldName: "startDate", required: true, type: "date", maxValue: insuranceDetails.endDate });
                rules.push({ fieldName: "endDate", required: true, type: "date", minValue: insuranceDetails.startDate });
            }

            if (wizardStep === 1) {
                const today = new Date();
                const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
                externalRules.push({ fieldName: "endDate", valid: insuranceDetails.endDate ? new Date(insuranceDetails.endDate) >= tomorrow : false, errorMessage: "Please upload your current insurance document" });
            }

            if (wizardStep === 2) {
                externalRules.push({ fieldName: "declarationSigned", valid: insuranceDetails.declarationSigned === true, errorMessage: "You must agree to the declaration to proceed" });
            }
        }

        validate(rules, insuranceDetails, externalRules);
    }

    function getInsuranceDetails() {
        LicenceCheckingService.getInsuranceDetails(employee, code)
            .then(
                (response) => {
                    setAccountName(response.accountName);
                    setRegistration(response.registration);
                    setDeclarationStepRequired(response.requiresDeclaration);
                    setDeclarationText(response.declarationText);
                    setRequiredCoverTypeIds(response.requiredCoverTypeIds ?? []);
                },
                () => {
                    setIsConsentError(true);
                }
            )
            .catch(() => {
                setIsConsentError(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    function getInsuranceCoverTypes() {
        const onError = () => {
            NotificationToaster.show(
                Intent.DANGER,
                "Could not load the types of insurance cover, please try again."
            );
        };

        GreyFleetService.getInsuranceCoverTypes()
            .then(
                (response) => {
                    setCoverTypes(response ?? []);
                },
                onError
            )
            .catch(onError)
            .finally(() => {
                setIsLoadingCoverTypes(false);
            });
    }

    // The menu ticks the options already chosen, so selecting one of those again clears it.
    function onCoverTypeSelect(item) {
        setInsuranceDetails((prev) => {
            let coverTypeIds;

            if (prev.coverTypeIds.includes(item.id)) {
                coverTypeIds = prev.coverTypeIds.filter((id) => id !== item.id);
            }
            else {
                coverTypeIds = [...prev.coverTypeIds, item.id];
            }

            return {
                ...prev,
                coverTypeIds,
            }
        });
    }

    // The tag input reports a removal using the rendered tag value, which is the cover type's name,
    // so it has to be resolved back to an id before it can be dropped from the selection.
    function onCoverTypeRemove(name) {
        const removedCoverType = coverTypes.find((coverType) => coverType.name === name);

        if (removedCoverType == null) {
            return;
        }

        setInsuranceDetails((prev) => {
            return {
                ...prev,
                coverTypeIds: prev.coverTypeIds.filter((id) => id !== removedCoverType.id),
            }
        });
    }

    function updateInsuranceDetails(key, value) {
        setInsuranceDetails((prev) => {
            return {
                ...prev,
                [key]: value,
            }
        });
    }

    function updateInsuranceDateDetails(key, item) {
        setInsuranceDetails((prev) => {
            return {
                ...prev,
                [key]: item ? moment(item).format("YYYY-MM-DD") : null,
            }
        });
    }

    function onFileChange(event) {
        var file = event.currentTarget.files[0];
        var filename = (event.currentTarget.files.length === 0) ? "" : file.name;

        setInsuranceDetails((prev) => {
            return {
                ...prev,
                file,
                filename,
            }
        });
    }

    function onWizardChange(step) {
        setWizardStep(step);
    }

    function readFileContents(file) {

        return new Promise((resolve, reject) => {

            var fileReader = new FileReader();

            if (file) {
                fileReader.readAsDataURL(file);
            } else {
                reject("No file provided");
            }

            fileReader.onload = function (event) {
                resolve(event.target.result);
            };
        })
    }

    function onWizardFinish() {
        setIsSaving(true);
        setWizardIsAllowedBack(false);

        readFileContents(insuranceDetails.file).then((fileContents) => {

            var fileExtension = insuranceDetails.filename.split('.').pop();
            var base64FileContents = fileContents.split(',')[1];

            let request = {
                odometerReading: insuranceDetails.odometerReading,
                declarationSigned: declarationStepRequired ? insuranceDetails.declarationSigned : true,
                token: code,
                insuranceDocument: {
                    ...insuranceDetails,
                    file: base64FileContents,
                    fileType: fileExtension,
                },
                employee
            };

            const handleApiError = (err) => {
                const validationError = 400;

                if (err?.response?.status === validationError && err.response.data != null) {
                    NotificationToaster.show(
                        Intent.WARNING,
                        err.response.data
                    );
                    return;
                } 

                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not save your insurance details, please try again."
                );
            };

            LicenceCheckingService.saveInsuranceDetails(request)
                .then(
                    () => {
                        setIsConsentSuccess(true);
                    },
                    handleApiError
                )
                .catch(handleApiError)
                .finally(() => {
                    setIsSaving(false);
                });
        }, () => {
            setIsSaving(false);
            setWizardIsAllowedBack(true);
            NotificationToaster.show(
                Intent.DANGER,
                "Could not read the selected file, please try again."
            );
        });
    }

    function refuseInsuranceUpload() {
        setIsSaving(true);

        let refuseInsuranceUploadRequest = {
            token: code,
            employee: employee,
        };

        const apiError = () => {
            NotificationToaster.show(
                Intent.DANGER,
                "Could not reject your insurance upload, please try again."
            );
        }

        LicenceCheckingService.rejectInsuranceUpload(refuseInsuranceUploadRequest)
            .then(() => {
                hideRefuseConsentModal();
                setIsConsentRejected(true);
            },
                apiError
            )
            .catch(apiError)
            .finally(() => {
                setIsSaving(false);
            });
    }

    function showRefuseConsentModal() {
        setIsRefuseConsentModalVisible(true);
    }

    function hideRefuseConsentModal() {
        setIsRefuseConsentModalVisible(false);
    }

    function getCantProceedTooltip() {
        switch (wizardStep) {
            case 0:
                return "Please confirm the registration number to proceed";
            case 1:
                return errors.coverTypeIds;
            case 2:
                return errors.declarationSigned;
            default:
                return undefined;
        }
    }

    // A dash under the header would read as "this check requires no cover", which is a different
    // thing entirely, so the block is withheld only when nothing at all resolves to a name.
    const showRequiredCoverTypes = resolvedRequiredCoverTypes.length > 0;

    return (
        <UnauthenticatedPage>
            <div className="licence-checking-form">
                <AlertConfirm
                    isOpen={isRefuseConsentModalVisible}
                    title="Refuse Upload"
                    onConfirm={refuseInsuranceUpload}
                    onCancel={hideRefuseConsentModal}
                    confirmButtonText="Refuse"
                    cancelButtonText="Cancel"
                >
                    <p>
                        Are you sure you want to refuse uploading your insurance details for
                        your employer to check? You will not be able to use your vehicle for business use.
                    </p>
                </AlertConfirm>

                <ShowHide
                    evaluator={isConsentSuccess}
                    show={<>
                        <h2>Insurance Details Submitted</h2>
                        <SuccessPanel>
                            <p>
                                Thank you for submitting your insurance details. <br />
                                You may now close this window.
                            </p>
                        </SuccessPanel>
                    </>}
                />
                <ShowHide
                    evaluator={isConsentRejected}
                    show={<FailurePanel title="Rejected Insurance Details">
                        <p>
                            You have decided to reject uploading your insurance details. <br />
                            You may now close this window.
                        </p>
                    </FailurePanel>}
                />

                <ShowHide
                    evaluator={isConsentError}
                    show={<FailurePanel title="Error With Insurance Details">
                        <p>
                            There was an error processing your link. <br />
                            The link has expired or you have already submitted your details.
                        </p>
                    </FailurePanel>}
                />

                <ShowHide
                    evaluator={
                        !isConsentSuccess &&
                        !isConsentRejected &&
                        !isConsentError
                    }
                    show={
                        <Fragment>
                            <h2>Your Insurance Details</h2>
                            <Wizard
                                onStepChange={onWizardChange}
                                onFinish={onWizardFinish}
                                canProceed={wizardValid}
                                cantProceedTooltip={getCantProceedTooltip()}
                                loading={isLoading}
                                disabled={isSaving}
                                allowBack={wizardIsAllowedBack}
                                alignButtonsRight
                                additionalButtons={
                                    <ShowHide
                                        evaluator={wizardStep === 0}
                                        show={
                                            <Button
                                                loading={isLoading}
                                                text="Refuse"
                                                intent="danger"
                                                onClick={showRefuseConsentModal}
                                            />
                                        }
                                    />
                                }
                            >
                                <div className="spacer-bottom">
                                    <h3
                                        className={classNames({
                                            "bp6-skeleton": isLoading,
                                        })}
                                    >
                                        {`Insurance Details Are Required for ${registration}`}
                                    </h3>
                                    <p
                                        className={classNames({
                                            "bp6-skeleton": isLoading,
                                        })}
                                    >
                                        As a responsible employer, {accountName}, have an obligation to ensure that you are legal to drive your personal vehicle '{registration}' for business purposes.
                                    </p>
                                    <p
                                        className={classNames({
                                            "bp6-skeleton": isLoading,
                                        })}
                                    >
                                        To complete the process, you will need:
                                    </p>
                                    <ul
                                        className={classNames({
                                            "bp6-skeleton": isLoading,
                                        })}
                                    >
                                        <li>A copy of your motor insurance document</li>
                                        <li>The latest mileage reading from your vehicle</li>
                                    </ul>
                                    <p
                                        className={classNames({
                                            "bp6-skeleton": isLoading,
                                        })}
                                    >
                                        To view a processing statement/privacy
                                        policy that summarises what Logistics UK
                                        will request on behalf of {accountName}{" "}
                                        how it will be used, and your rights in
                                        respect of that data at any point,{" "}
                                        <a
                                            target="_blank"
                                            rel="noreferrer"
                                            href="https://logistics.org.uk/logistics-uk-privacy-policy/"
                                        >
                                            click here
                                        </a>
                                        .
                                    </p>
                                    <FormCheckbox
                                        id="insurance-details-required"
                                        label="I confirm the registration number listed is my vehicle and is the vehicle I will be using for business purposes"
                                        checked={isRegConfirmed}
                                        onChange={(e) => setIsRegConfirmed(e.target.checked)}
                                    />
                                    <p
                                        className={classNames({
                                            "bp6-skeleton": isLoading,
                                        })}
                                    >
                                        Click the next button below to begin the
                                        process. If the above
                                        criteria does not apply to you then you
                                        can click refuse.
                                    </p>
                                </div>
                                <div>
                                    <h3>Insurance Details</h3>
                                    <GridLayout numberOfColumns={2} gap={40}>
                                        <FormDateInput
                                            id="insurance-start-date"
                                            headingText="Policy Start Date"
                                            loading={isLoading}
                                            fill
                                            onChange={(item) => updateInsuranceDateDetails("startDate", item)}
                                            value={insuranceDetails.startDate}
                                            dangerHelperText={errors.startDate}
                                        />
                                        <FormDateInput
                                            id="insurance-end-date"
                                            headingText="Policy End Date"
                                            loading={isLoading}
                                            fill
                                            onChange={(item) => updateInsuranceDateDetails("endDate", item)}
                                            value={insuranceDetails.endDate}
                                            dangerHelperText={errors.endDate}
                                        />
                                    </GridLayout>
                                    <GridLayout numberOfColumns={2} gap={40}>
                                        <div className="spacer-bottom-large">
                                            <FormMultiSelect
                                                id="insurance-cover-types"
                                                fill
                                                headingText="What type(s) of use does your insurance cover?"
                                                items={coverTypes}
                                                loading={isLoading || isLoadingCoverTypes}
                                                placeholder="Please select"
                                                selectedValues={insuranceDetails.coverTypeIds.map((id) => ({ id }))}
                                                onItemSelect={onCoverTypeSelect}
                                                onRemove={onCoverTypeRemove}
                                                dangerHelperText={errors.coverTypeIds}
                                            />
                                            <ShowHide
                                                evaluator={showRequiredCoverTypes}
                                                show={
                                                    <TypesOfCoverRecord
                                                        loading={isLoading || isLoadingCoverTypes}
                                                        header="Required type(s) of cover"
                                                        coverTypeIds={resolvedRequiredCoverTypeIds}
                                                        coverTypes={coverTypes}
                                                    />
                                                }
                                            />
                                        </div>
                                        <FormFileInput
                                            id="insurance-document"
                                            headingText="Upload a copy of your motor insurance"
                                            loading={isLoading}
                                            fill
                                            onChange={onFileChange}
                                            hasSelection={insuranceDetails.file && insuranceDetails.filename !== ''}
                                            helperText="File formats accepted are .pdf, .jpg and .png"
                                            text={insuranceDetails.filename == null ? "Select document" : insuranceDetails.filename}
                                            dangerHelperText={errors.filename}
                                            acceptedMimeTypes={['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']}
                                        />
                                    </GridLayout>
                                    <h3>Vehicle Details</h3>
                                    <GridLayout numberOfColumns={2} gap={40}>
                                        <div>
                                            <FormNumericInput
                                                id="insurance-odometer-reading"
                                                headingText="Latest Mileage Reading"
                                                loading={isLoading}
                                                selectedValue={insuranceDetails.odometerReading}
                                                fill
                                                min={1}
                                                max={999999}
                                                onValueChange={(item) => updateInsuranceDetails("odometerReading", item === 0 ? null : item)}
                                                dangerHelperText={errors.odometerReading}
                                            />
                                        </div>
                                    </GridLayout>
                                </div>
                                {declarationStepRequired &&
                                    <div className="spacer-bottom">
                                        <div className="spacer-bottom">
                                            <h3>Declaration</h3>
                                            <div className="spacer-bottom">
                                                <p>
                                                    {declarationText}
                                                </p>
                                            </div>
                                            <FormCheckbox
                                                id="insurance-declaration"
                                                label="I agree to the above declaration and am happy for Logistics UK to process my data on behalf of my employer"
                                                loading={isLoading}
                                                checked={insuranceDetails.declarationSigned}
                                                onChange={(e) => updateInsuranceDetails("declarationSigned", e.target.checked)}
                                            />
                                        </div>

                                    </div>
                                }

                            </Wizard>
                        </Fragment>
                    }
                />
            </div>
        </UnauthenticatedPage>
    );
}
